/**
 * `gamification-core` — domain CONFIG for comandero (restaurant POS).
 *
 * OPT-IN and NON-punitive retrofit (B2B, TEAM-oriented): gamification
 * celebrates shift good service (closed orders, correct cashbox cash-up,
 * consistency of service days) as team ENGAGEMENT — never as
 * surveillance of person. Private leagues by default (§leagues): not shown
 * comparativas entre personas.
 *
 * Gamification DERIVES from `activity` (`activity_recent` bucket) — see
 * `derive.ts`. Does not alter log or add tables: pure projection and
 * recomputable.
 */
import { counterAtLeast, streakAtLeast } from "./badges";
import type { GamificationConfig } from "./engine";

/** Domain event types (derived from the `activity` feed). */
export const COMANDERO_EVENTS = {
  /** An order was charged/closed (status → "paid"). */
  orderClosed: "order.closed",
  /** A shift cashbox session was closed (cashed up) (status → "closed"). */
  shiftReconciled: "shift.reconciled",
} as const;

/** Streak line: days with at least one closed order (team consistency). */
export const SERVICE_STREAK_LINE = "service";

export const comanderoGamificationConfig: GamificationConfig = {
  xp: {
    [COMANDERO_EVENTS.shiftReconciled]: 30,
    [COMANDERO_EVENTS.orderClosed]: 5,
  },
  streakLines: {
    [COMANDERO_EVENTS.orderClosed]: [SERVICE_STREAK_LINE],
  },
  counters: {
    [COMANDERO_EVENTS.orderClosed]: ["orders_closed"],
    [COMANDERO_EVENTS.shiftReconciled]: ["shifts_reconciled"],
  },
  badges: [
    {
      id: "first-order",
      title: "First order",
      description: "Close your first order.",
      when: counterAtLeast("orders_closed", 1),
    },
    {
      id: "first-shift",
      title: "First shift closed",
      description: "Close and reconcile your first cash session.",
      when: counterAtLeast("shifts_reconciled", 1),
    },
    {
      id: "hundred-orders",
      title: "Hundred orders",
      description: "Close one hundred orders as a team.",
      when: counterAtLeast("orders_closed", 100),
    },
    {
      id: "steady-week",
      title: "Steady week",
      description: "Serve orders seven days in a row.",
      when: streakAtLeast(SERVICE_STREAK_LINE, 7),
      bonusXp: 50,
    },
    {
      id: "balanced",
      title: "Balanced",
      description: "Reconcile ten cash sessions.",
      when: counterAtLeast("shifts_reconciled", 10),
    },
  ],
  quests: [
    {
      id: "daily-orders",
      title: "Close ten orders today",
      period: "daily",
      goals: [{ metric: COMANDERO_EVENTS.orderClosed, target: 10 }],
      bonusXp: 20,
    },
  ],
};
